'use client';
/* eslint-disable @next/next/no-img-element -- collections prototype: <img> for R2/idb blob URLs, not the app image pipeline */ // ---------------------------------------------------------------------------
// Collections — the inspector views over one collection's assets.
//
//   Gallery — native-aspect (or dense square) frames, optional Group by prop.
//   Matrix  — pivot two properties; cells are BUCKETS (×N badge); empty cells
//             are coverage gaps with a generate (+) affordance (ephemeral).
//   Table   — record grid: frozen thumb+name, a column per property, sortable,
//             row selection, inline cell editing.
//
// All views render a pre-filtered LibAsset[] and share one selection Set of
// asset ids, so switching views keeps your filter and selection. Editing is
// real — it flows to the persistent library store. Monochrome, ghost hover,
// no dashed borders.
// ---------------------------------------------------------------------------
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { createPortal } from 'react-dom';
import { Braces, Check, ChevronLeft, ChevronRight, Download, GripVertical, Loader2, Pencil, Plus, X, ChevronUp, ChevronDown } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { ImageZoomControls } from '@/components/generation/image/ImageZoomControls';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { libraryActions, getAssetMediaUrl, getAssetUrl, useLibraryStore, type LibAsset, type Property } from '@/store/libraryStore';
import { trackLibraryAssetDownloaded } from '@/lib/analytics';
import type { AttrValue } from '@/types/shared';
import { assetInGroup, coerceInput, compareAttr, facetTokenLabel, facetTokens, formatAttrValue, hasAttrValue, isCheckboxLeftover } from './propertyValues';
import { PropertyTypeRows, useCollectionPropertyTypesEnabled } from './controls';
// Lightbox zoom — a multiplier on the fit-to-view size (1 = fit), matching the
// edit/view lightbox so zoom reads the same everywhere.
const MAX_ZOOM = 8;
/** Clamp pan so the zoomed image always covers the viewport (no empty gutters). */ function clampPan(pan: {
    x: number;
    y: number;
}, zoom: number, box: HTMLElement): {
    x: number;
    y: number;
} {
    const maxX = ((Math.max(zoom, 1) - 1) * box.clientWidth) / 2;
    const maxY = ((Math.max(zoom, 1) - 1) * box.clientHeight) / 2;
    return {
        x: Math.max(-maxX, Math.min(maxX, pan.x)),
        y: Math.max(-maxY, Math.min(maxY, pan.y))
    };
}
const KEY_TABLE_COLW = 'martini-library-table-column-widths';
// Gallery display axes — independent size and crop (values are their own labels).
export const GALLERY_SIZES = [
    'Small',
    'Medium',
    'Large'
] as const;
export type GallerySize = (typeof GALLERY_SIZES)[number];
export const GALLERY_CROPS = [
    'Square',
    'Original'
] as const;
export type GalleryCrop = (typeof GALLERY_CROPS)[number];
/** Payload the canvas drop-glue reads to place a library asset onto a board. */ function KindBadge({ kind }: {
    kind: LibAsset['kind'];
}) {
    const t = useTranslations$1();
    if (kind !== 'model3d' && kind !== 'video') return null;
    return (<span className="absolute bottom-1 right-1 rounded bg-background/80 px-1 text-[10px] uppercase text-muted-foreground">
      {kind === 'model3d' ? t("s9xNrU") : t("zAamQ_")}
    </span>);
}
/** Quiet tile for a text (prompt-fragment) asset — it has no pixels, so it
    reads as its name over a couple of clamped lines of body. `compact` is the
    icon-only variant for tiny thumbnails (table rows, off-grid chips). Fills
    its bordered wrapper; caller owns the footprint (aspect-square / row height). */ export function TextTile({ asset, compact }: {
    asset: LibAsset;
    compact?: boolean;
}) {
    if (compact) {
        return (<div className="flex h-full w-full items-center justify-center bg-secondary/40 text-muted-foreground/60">
        <Braces className="size-3.5"/>
      </div>);
    }
    return (<div className="flex h-full w-full flex-col gap-1 overflow-hidden bg-secondary/40 p-2 text-left">
      <span className="truncate text-xs font-medium text-foreground/80">{asset.name}</span>
      <span className="line-clamp-4 whitespace-pre-wrap text-[10px] leading-snug text-muted-foreground">
        {asset.textValue}
      </span>
    </div>);
}
function SelectDot({ selected, onToggle }: {
    selected: boolean;
    onToggle: (e: React.MouseEvent) => void;
}) {
    const t = useTranslations$1();
    return (<button onClick={onToggle} aria-label={selected ? t("7thI9m") : t("kQAf2d")} className={cn('absolute left-1 top-1 flex size-4 items-center justify-center rounded border transition-opacity', selected ? 'border-foreground bg-foreground text-background opacity-100' : 'border-border bg-background/80 text-transparent opacity-0 group-hover:opacity-100')}>
      <Check className="size-3"/>
    </button>);
}
// --- Gallery ---------------------------------------------------------------
function Frame({ asset, crop, selected, onToggle, onContext, onOpen, onOpenFull, dragIds, readOnly }: {
    asset: LibAsset;
    crop: GalleryCrop;
    selected: boolean;
    onToggle: (event: React.MouseEvent) => void;
    onContext?: (e: React.MouseEvent) => void;
    /** Single click — select & inspect (right panel). */ onOpen?: () => void;
    /** Double click — full-bleed lightbox. */ onOpenFull?: () => void;
    dragIds: string[];
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    const square = crop === 'Square';
    const [captionEditing, setCaptionEditing] = useState(false);
    return (<div className="group break-inside-avoid" data-asset-id={asset.id}>
      <div onContextMenu={onContext} onClick={onOpen} onDoubleClick={onOpenFull} draggable={!readOnly} onDragStart={(e)=>{
        // Move-to-collection drag: the rail rows consume this (in-library
        // move; multi-select rides along when the dragged tile is selected).
        e.dataTransfer.setData('application/x-martini-library-move', asset.id);
        if (dragIds.length > 1) {
            e.dataTransfer.setData('application/x-martini-library-move-many', JSON.stringify({
                ids: dragIds
            }));
        }
        e.dataTransfer.effectAllowed = 'move';
    }} className={cn('relative cursor-pointer overflow-hidden rounded-lg border bg-muted/30 transition duration-150', selected ? 'border-foreground/70 ring-1 ring-foreground/70' : 'border-border/70 hover:border-foreground/25 hover:shadow-lg hover:shadow-black/30')}>
        { /* draggable={false} so a grab that starts ON the image sources the
            tile's drag (with its move payload) instead of the browser's
            native image drag — grab anywhere, not just the grip. */ }
        {asset.kind === 'text' ? (<div className="aspect-square w-full">
            <TextTile asset={asset}/>
          </div>) : square ? (<img src={getAssetUrl(asset)} alt="" loading="lazy" draggable={false} className="aspect-square w-full cursor-grab object-cover"/>) : (<img src={getAssetUrl(asset, 'large')} alt="" loading="lazy" draggable={false} className="block h-auto w-full cursor-grab"/>)}
        <SelectDot selected={selected} onToggle={(e)=>{
        e.stopPropagation();
        onToggle(e);
    }}/>
        {!readOnly && (<span className="absolute right-1.5 top-1.5 rounded-md bg-background/70 p-1 text-muted-foreground/70 opacity-0 transition-opacity group-hover:opacity-100">
            <GripVertical className="size-3"/>
          </span>)}
        <KindBadge kind={asset.kind}/>
      </div>
      {captionEditing && !readOnly ? (<input autoFocus data-quiet-focus defaultValue={asset.name} onBlur={(e)=>{
        const n = e.target.value.trim();
        if (n && n !== asset.name) libraryActions.renameAsset(asset.id, n);
        setCaptionEditing(false);
    }} onKeyDown={(e)=>{
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setCaptionEditing(false);
    }} onClick={(e)=>e.stopPropagation()} className="mt-1.5 w-full rounded bg-secondary px-1 py-0.5 text-xs text-foreground outline-none"/>) : (<div title={readOnly ? `${asset.name} — read-only` : t("uk9jWc", {
        name: asset.name
    })} onDoubleClick={(e)=>{
        if (readOnly) return;
        e.stopPropagation();
        setCaptionEditing(true);
    }} className={cn('mt-1.5 truncate px-0.5 text-xs text-foreground/70 transition-colors group-hover:text-foreground', !readOnly && 'cursor-text')}>
          {asset.name}
        </div>)}
    </div>);
}
export function GalleryView({ assets, size, crop, groupBy, groupProp, groupValues, groupLabel, valueLabel, selection, onToggle, onAddAsset, onAssetContext, onOpenAsset, onOpenAssetFull, readOnly }: {
    assets: LibAsset[];
    size: GallerySize;
    crop: GalleryCrop;
    groupBy: string | null;
    /** The group-by property (for typed group membership); undefined = no grouping. */ groupProp?: Property;
    /** Ordered facet tokens present for the group-by property. */ groupValues: string[];
    groupLabel: string;
    /** Resolve a group-by token to its display label. */ valueLabel: (token: string) => string;
    selection: Set<string>;
    onToggle: (id: string, event: React.MouseEvent) => void;
    onAddAsset?: () => void;
    /** Right-click an asset: the caller opens its context menu (actions live on the thing, not in a dock). */ onAssetContext?: (e: React.MouseEvent, id: string) => void;
    /** Click an asset: select & inspect (right panel). */ onOpenAsset?: (id: string) => void;
    /** Double-click an asset: full-bleed lightbox. */ onOpenAssetFull?: (id: string) => void;
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    // Nullish-guard the lookup — size comes straight from localStorage.
    const cols = {
        Small: 'columns-6',
        Medium: 'columns-4',
        Large: 'columns-3'
    }[size] ?? 'columns-4';
    const renderBand = (list: LibAsset[], lead?: React.ReactNode)=>(<div className={cn(cols, 'gap-2.5 [&>*]:mb-2.5')}>
      {lead}
      {list.map((a)=>(<Frame key={a.id} asset={a} crop={crop} selected={selection.has(a.id)} dragIds={selection.has(a.id) && selection.size > 1 ? [
                ...selection
            ] : [
                a.id
            ]} onToggle={(event)=>onToggle(a.id, event)} onContext={onAssetContext ? (e)=>onAssetContext(e, a.id) : undefined} onOpen={onOpenAsset ? ()=>onOpenAsset(a.id) : undefined} onOpenFull={onOpenAssetFull ? ()=>onOpenAssetFull(a.id) : undefined} readOnly={readOnly}/>))}
    </div>);
    // The add affordance is a TILE in the grid — same size and rhythm as the
    // assets, and it LEADS (top-left) so adding is never below the fold.
    const addTile = onAddAsset ? (<button onClick={onAddAsset} className="mb-2.5 flex aspect-square w-full break-inside-avoid flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border/60 text-muted-foreground/50 transition duration-150 hover:border-foreground/25 hover:bg-secondary/20 hover:text-muted-foreground">
      <Plus className="size-4" strokeWidth={1.5}/>
      <span className="text-[10px] font-medium uppercase tracking-wider">{t("0t8z1W")}</span>
    </button>) : null;
    if (!groupBy || !groupProp) {
        return <div>{renderBand(assets, assets.length === 0 ? addTile : undefined)}</div>;
    }
    // An asset with no tokens for this property is off-grid.
    const gp = groupProp;
    const unassigned = assets.filter((a)=>facetTokens(gp, a.attrs[groupBy]).length === 0);
    return (<div className="space-y-4">
      {groupValues.map((val)=>{
        const band = assets.filter((a)=>assetInGroup(gp, a.attrs[groupBy], val));
        if (!band.length) return null;
        return (<div key={val}>
            <div className="sticky top-0 z-10 mb-1.5 flex items-center gap-1.5 bg-card py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
              { /* eslint-disable-next-line i18next/no-literal-string -- decorative middot joining two already-translated labels, not copy */ }
              {groupLabel} · {valueLabel(val)}
              <span className="tabular-nums text-muted-foreground/50">{band.length}</span>
            </div>
            {renderBand(band)}
          </div>);
    })}
      {unassigned.length > 0 && (<div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
            {t("UOjqaz")} <span className="tabular-nums text-muted-foreground/50">{unassigned.length}</span>
          </div>
          {renderBand(unassigned)}
        </div>)}
    </div>);
}
// --- Matrix ----------------------------------------------------------------
export function MatrixView({ assets, rowKey, colKey, rowValues, colValues, rowLabel, valueLabel, selection, onAssetContext, onOpenAsset, onOpenAssetFull }: {
    assets: LibAsset[];
    rowKey: string;
    colKey: string;
    /** Option ids present on each axis, in order. */ rowValues: string[];
    colValues: string[];
    rowLabel: string;
    /** Resolve an axis option id to its display label. */ valueLabel: (key: string, optionId: string) => string;
    selection: Set<string>;
    onAssetContext?: (e: React.MouseEvent, id: string) => void;
    onOpenAsset?: (id: string) => void;
    onOpenAssetFull?: (id: string) => void;
}) {
    const t = useTranslations$1();
    const cell = 'minmax(60px,1fr)';
    const offGrid = assets.filter((a)=>!a.attrs[rowKey] || !a.attrs[colKey]);
    return (<div className="overflow-x-auto pb-1">
      <div className="grid gap-1" style={{
        gridTemplateColumns: `minmax(48px, auto) repeat(${colValues.length}, ${cell})`
    }}>
        <div className="sticky left-0 z-10 bg-card text-[10px] text-muted-foreground/50">
          {rowLabel.slice(0, 4)}
          {t("DIhR8k")}
        </div>
        {colValues.map((cv)=>(<div key={cv} className="truncate px-0.5 pb-0.5 text-center text-[10px] text-muted-foreground">
            {valueLabel(colKey, cv)}
          </div>))}
        {rowValues.map((rv)=>(<div key={rv} className="contents">
            <div className="sticky left-0 z-10 flex items-center truncate bg-card pr-1 text-[10px] text-muted-foreground">
              {valueLabel(rowKey, rv)}
            </div>
            {colValues.map((cv)=>{
            const members = assets.filter((a)=>a.attrs[rowKey] === rv && a.attrs[colKey] === cv);
            // Empty cell = a coverage gap, stated plainly. (Generate-to-fill
            // returns when memory-conditioned generation lands server-side.)
            if (!members.length) return (<div key={cv} title={t("NEGKbg", {
                row: valueLabel(rowKey, rv),
                col: valueLabel(colKey, cv)
            })} className="aspect-square rounded border border-border bg-secondary/20"/>);
            const a = members[0];
            const sel = selection.has(a.id);
            return (<div key={cv} onContextMenu={onAssetContext ? (e)=>onAssetContext(e, a.id) : undefined} onClick={onOpenAsset ? ()=>onOpenAsset(a.id) : undefined} title={members.length > 1 ? t("62vPPm", {
                row: valueLabel(rowKey, rv),
                col: valueLabel(colKey, cv),
                count: String(members.length)
            }) : t("7SISrm", {
                row: valueLabel(rowKey, rv),
                col: valueLabel(colKey, cv)
            })} className={cn('group relative aspect-square cursor-grab overflow-hidden rounded border bg-muted/40', sel ? 'border-foreground ring-1 ring-inset ring-foreground' : 'border-border')}>
                  {a.kind === 'text' ? (<TextTile asset={a}/>) : (<img src={getAssetUrl(a)} alt="" loading="lazy" className="h-full w-full object-cover"/>)}
                  {members.length > 1 && (<span className="absolute bottom-0.5 right-0.5 rounded bg-background/80 px-1 text-[10px] tabular-nums text-muted-foreground">
                      {t("zcNq_R")}
                      {members.length}
                    </span>)}
                  <KindBadge kind={a.kind}/>
                </div>);
        })}
          </div>))}
      </div>
      {offGrid.length > 0 && (<div className="mt-3 border-t border-border pt-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
            {t("077utk")} <span className="tabular-nums">{offGrid.length}</span>
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {offGrid.map((a)=>(<div key={a.id} className="relative size-12 shrink-0 cursor-grab overflow-hidden rounded border border-border" title={a.name}>
                {a.kind === 'text' ? (<TextTile asset={a} compact/>) : (<img src={getAssetUrl(a)} alt="" loading="lazy" className="h-full w-full object-cover"/>)}
                <KindBadge kind={a.kind}/>
              </div>))}
          </div>
        </div>)}
    </div>);
}
// --- Table -----------------------------------------------------------------
/** Header-edge drag handle for column resize. */ function ColGrip({ onMouseDown }: {
    onMouseDown: (e: React.MouseEvent) => void;
}) {
    return (<span onMouseDown={onMouseDown} onClick={(e)=>e.stopPropagation()} className="absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize select-none after:absolute after:inset-y-1 after:left-1/2 after:w-px after:bg-transparent hover:after:bg-foreground/30"/>);
}
function Th({ k, label, sort, onSort, className }: {
    k: string;
    label: string;
    sort: {
        by: string;
        dir: 'asc' | 'desc';
    };
    onSort: (k: string) => void;
    className?: string;
}) {
    const SortIcon = sort.by === k ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : null;
    return (<button onClick={()=>onSort(k)} className={cn('whitespace-nowrap px-2 py-1 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 hover:text-foreground', className)}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {SortIcon && <SortIcon className="size-3 text-muted-foreground"/>}
      </span>
    </button>);
}
/** Single floating value-picker for table cells (one instance, context-menu
 * style — a Radix popover per cell would be hundreds of mounts). Rows are the
 * property's live options; "New value" grows it in place, and pencil-on-hover
 * renames an option (spec §4: one write re-labels it everywhere). */ function CellEditor({ label, options, currentId, x, y, onPick, onRename, onDone }: {
    label: string;
    options: {
        id: string;
        value: string;
    }[];
    currentId?: string;
    x: number;
    y: number;
    /** Set to this option value (creating on new), or null to clear. */ onPick: (value: string | null) => void;
    onRename: (optionId: string, value: string) => void;
    onDone: () => void;
}) {
    const t = useTranslations$1();
    const [draft, setDraft] = useState('');
    const [renaming, setRenaming] = useState<{
        id: string;
        value: string;
    } | null>(null);
    return createPortal(<>
      <div className="fixed inset-0 z-[70]" onClick={onDone}/>
      <div style={{
        left: Math.min(x, window.innerWidth - 190),
        top: Math.min(y + 2, window.innerHeight - 260)
    }} className="fixed z-[71] w-44 rounded-md border border-border bg-popover p-1 shadow-xl">
        <div className="px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
          {label}
        </div>
        <div className="max-h-44 overflow-auto">
          {options.map((o)=>renaming?.id === o.id ? (<input key={o.id} autoFocus value={renaming.value} onChange={(e)=>setRenaming({
                id: o.id,
                value: e.target.value
            })} onKeyDown={(e)=>{
            if (e.key === 'Enter' && renaming.value.trim()) {
                onRename(o.id, renaming.value.trim());
                setRenaming(null);
            }
            if (e.key === 'Escape') setRenaming(null);
        }} onBlur={()=>setRenaming(null)} className="my-px w-full rounded bg-secondary px-2 py-1 text-xs text-foreground outline-none"/>) : (<div key={o.id} className="group/opt flex items-center rounded hover:bg-secondary">
                <button onClick={()=>onPick(o.value)} className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1 text-xs text-foreground">
                  <Check className={cn('size-3 shrink-0', currentId === o.id ? 'opacity-100' : 'opacity-0')}/>
                  <span className="truncate">{o.value}</span>
                </button>
                <button onClick={()=>setRenaming({
                id: o.id,
                value: o.value
            })} title={t("QyMrXe")} className="mr-1 shrink-0 rounded p-1 text-transparent hover:bg-background/50 group-hover/opt:text-muted-foreground">
                  <Pencil className="size-3"/>
                </button>
              </div>))}
          {options.length === 0 && (<div className="px-2 py-1 text-xs text-muted-foreground/50">{t("OHApZq")}</div>)}
        </div>
        {currentId && (<button onClick={()=>onPick(null)} className="flex w-full items-center gap-2 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
            <X className="size-3 shrink-0"/> {t("_GCoTA")}
          </button>)}
        <div className="mt-1 border-t border-border pt-1">
          <input autoFocus={options.length === 0} value={draft} onChange={(e)=>setDraft(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter' && draft.trim()) onPick(draft.trim());
        if (e.key === 'Escape') onDone();
    }} placeholder={t("Hf3q3k")} className="w-full rounded bg-secondary/50 px-2 py-1 text-xs text-foreground outline-none placeholder:text-muted-foreground/50"/>
        </div>
      </div>
    </>, document.body);
}
export function TableView({ assets, columns, properties, groupBy, groupProp, groupValues = [], size = 'Medium', propLabel, columnOptions, hiddenCount, showingAll, onToggleCols, onSetAttr, onSetValueRaw, onRenameOption, onAddProperty, propertyTypesEnabled: propertyTypesEnabledProp, sort, onSort, selection, onToggle, onAssetContext, onOpenAsset, onOpenAssetFull, readOnly }: {
    assets: LibAsset[];
    /** Property keys to render as columns (discriminating set, or all when expanded). */ columns: string[];
    /** Full property set — resolves a column's type/config for typed cells. */ properties: Property[];
    /** Property key to group rows under section headers (same control as gallery). */ groupBy?: string;
    /** The group-by property (typed group membership); undefined = no grouping. */ groupProp?: Property;
    /** Ordered facet tokens present for the group-by property (same as the gallery). */ groupValues?: string[];
    /** Row density — shares the gallery size control. */ size?: 'Small' | 'Medium' | 'Large';
    propLabel: (key: string) => string;
    /** Options ({id,value}) for a property column — drives select cell display, ordering, and the editor. */ columnOptions: (key: string) => {
        id: string;
        value: string;
    }[];
    hiddenCount: number;
    showingAll: boolean;
    onToggleCols: () => void;
    /** Select cell edit (label → option, creating on new). */ onSetAttr: (assetId: string, key: string, value: string | null) => void;
    /** Typed cell edit (number / checkbox / text). */ onSetValueRaw: (assetId: string, key: string, value: AttrValue | null) => void;
    /** Rename an option value in place — re-labels every asset that holds it (spec §4). */ onRenameOption: (key: string, optionId: string, value: string) => void;
    onAddProperty: (label: string, type?: Property['type']) => void;
    propertyTypesEnabled?: boolean;
    /** Sort lives in the view definition; the header toggles it via onSort. */ sort: {
        by: string;
        dir: 'asc' | 'desc';
    };
    onSort: (key: string) => void;
    selection: Set<string>;
    onToggle: (id: string, event: React.MouseEvent) => void;
    onAssetContext?: (e: React.MouseEvent, id: string) => void;
    onOpenAsset?: (id: string) => void;
    onOpenAssetFull?: (id: string) => void;
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    const [edit, setEdit] = useState<{
        assetId: string;
        key: string;
        x: number;
        y: number;
    } | null>(null);
    const [newPropOpen, setNewPropOpen] = useState(false);
    // Per-column widths (name + each property key), draggable at the header edge
    // and shared across every table via localStorage.
    const [colW, setColW] = useState<Record<string, number>>(()=>{
        if (typeof window === 'undefined') return {};
        try {
            return JSON.parse(localStorage.getItem(KEY_TABLE_COLW) ?? '{}') as Record<string, number>;
        } catch  {
            return {};
        }
    });
    const wOf = (k: string, dflt: number)=>Math.min(560, Math.max(80, colW[k] ?? dflt));
    const startColResize = (k: string, dflt: number)=>(e: React.MouseEvent)=>{
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startW = wOf(k, dflt);
            const move = (ev: MouseEvent)=>setColW((w)=>({
                        ...w,
                        [k]: Math.min(560, Math.max(80, startW + ev.clientX - startX))
                    }));
            const up = ()=>{
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
                setColW((w)=>{
                    if (typeof window !== 'undefined') localStorage.setItem(KEY_TABLE_COLW, JSON.stringify(w));
                    return w;
                });
            };
            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        };
    const [newPropName, setNewPropName] = useState('');
    const fallbackPropertyTypesEnabled = useCollectionPropertyTypesEnabled();
    const propertyTypesEnabled = propertyTypesEnabledProp ?? fallbackPropertyTypesEnabled;
    const [newType, setNewType] = useState<Property['type']>(propertyTypesEnabled ? 'select' : 'text');
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const propByKey = (key: string): Property | undefined =>properties.find((p)=>p.key === key);
    const mixedKinds = new Set(assets.map((a)=>a.kind)).size > 1;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const sorted = [
        ...assets
    ].sort((a, b)=>{
        let cmp = 0;
        if (sort.by === 'name') cmp = a.name.localeCompare(b.name);
        else if (sort.by === 'kind') cmp = a.kind.localeCompare(b.kind);
        else {
            const prop = propByKey(sort.by);
            cmp = prop ? compareAttr(prop, a.attrs[sort.by], b.attrs[sort.by]) : 0;
        }
        return cmp * dir;
    });
    // Grouped rows: one section per present token, ungrouped last — the same
    // type-agnostic token model the gallery uses for the same control.
    const groups: {
        label: string | null;
        rows: LibAsset[];
    }[] = groupBy && groupProp ? [
        ...groupValues.map((tk)=>({
                label: facetTokenLabel(groupProp, tk),
                rows: sorted.filter((a)=>assetInGroup(groupProp, a.attrs[groupBy], tk))
            })).filter((g)=>g.rows.length),
        ...(()=>{
            const rest = sorted.filter((a)=>facetTokens(groupProp, a.attrs[groupBy]).length === 0);
            return rest.length ? [
                {
                    label: 'None' as string | null,
                    rows: rest
                }
            ] : [];
        })()
    ] : [
        {
            label: null,
            rows: sorted
        }
    ];
    // Row density shares the gallery size control.
    const rowPad = size === 'Small' ? 'py-0.5' : size === 'Large' ? 'py-2' : 'py-1';
    const thumbSize = size === 'Small' ? 'size-5' : size === 'Large' ? 'size-10' : 'size-6';
    return (<div className="no-scrollbar overflow-x-auto text-xs">
      { /* Rows span the FULL surface even with few columns (w-max min-w-full) —
          no sticky column: its opaque background read as a floating block
          against the page, and ≤ a handful of columns rarely scroll anyway. */ }
      <div className="w-max min-w-full">
        { /* header */ }
        <div className="flex items-center border-b border-border">
          <div className="w-8"/>
          <div className="relative shrink-0" style={{
        width: wOf('name', 208)
    }}>
            <Th k="name" label={t("HAlOn1")} sort={sort} onSort={onSort} className="w-full"/>
            <ColGrip onMouseDown={startColResize('name', 208)}/>
          </div>
          {columns.map((key)=>(<div key={key} className="relative shrink-0" style={{
            width: wOf(key, 112)
        }}>
              <Th k={key} label={propLabel(key)} sort={sort} onSort={onSort}/>
              <ColGrip onMouseDown={startColResize(key, 112)}/>
            </div>))}
          {mixedKinds && (<div className="w-16">
              <Th k="kind" label={t("e5x8FT")} sort={sort} onSort={onSort}/>
            </div>)}
          <div className="flex w-32 items-center gap-1 py-0.5 pl-1">
            {hiddenCount > 0 && (<button onClick={onToggleCols} title={showingAll ? t("xZG9vp") : t("3Za78E")} className="whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 hover:bg-secondary hover:text-foreground">
                {showingAll ? t("mFYgAX") : <span className="tabular-nums">+{hiddenCount}</span>}
              </button>)}
            {!readOnly && (<Popover open={newPropOpen} onOpenChange={(open)=>{
        setNewPropOpen(open);
        if (open) {
            setNewPropName('');
            setNewType(propertyTypesEnabled ? 'select' : 'text');
        }
    }}>
                <PopoverTrigger asChild>
                  <button className="whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 hover:bg-secondary hover:text-foreground">
                    {t("G33p3m")}
                  </button>
                </PopoverTrigger>
                { /* Just a name by default; the gated type picker is available only
                  when the collection property-types lab is enabled. */ }
                <PopoverContent side="bottom" align="end" className="w-60 p-1">
                  <input autoFocus data-quiet-focus value={newPropName} onChange={(e)=>setNewPropName(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter' && newPropName.trim()) {
            onAddProperty(newPropName.trim(), propertyTypesEnabled ? newType : 'text');
            setNewPropOpen(false);
        }
    }} placeholder={t("v1I4CA")} className="mb-1 w-full rounded-md bg-secondary/60 px-2 py-1.5 text-xs text-foreground outline-none placeholder:text-muted-foreground/50"/>
                  {propertyTypesEnabled && <PropertyTypeRows selected={newType} onSelect={setNewType}/>}
                  <div className="mt-1 border-t border-border pt-1">
                    <button disabled={!newPropName.trim()} onClick={()=>{
        onAddProperty(newPropName.trim(), propertyTypesEnabled ? newType : 'text');
        setNewPropOpen(false);
    }} className="w-full rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-secondary disabled:cursor-default disabled:text-muted-foreground/40 disabled:hover:bg-transparent">
                      {t("VnQX3t")}
                    </button>
                  </div>
                </PopoverContent>
              </Popover>)}
          </div>
        </div>
        { /* rows — grouped under section headers when a group-by is set */ }
        {groups.map((g)=>(<Fragment key={g.label ?? '~all'}>
            {g.label !== null && (<div className="flex items-center gap-1.5 border-b border-border/50 bg-secondary/20 py-1 pl-8 pr-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                {g.label}
                <span className="tabular-nums text-muted-foreground/40">{g.rows.length}</span>
              </div>)}
            {g.rows.map((a)=>{
            const sel = selection.has(a.id);
            return (<div key={a.id} onContextMenu={onAssetContext ? (e)=>onAssetContext(e, a.id) : undefined} onClick={onOpenAsset ? ()=>onOpenAsset(a.id) : undefined} className={cn('group flex cursor-default items-center border-b border-border/50', sel ? 'bg-secondary/60' : 'hover:bg-secondary/30')}>
                  <button onClick={(e)=>{
                e.stopPropagation();
                onToggle(a.id, e);
            }} title={t("kQAf2d")} className="flex w-8 items-center justify-center self-stretch">
                    <span className={cn('flex size-3.5 items-center justify-center rounded border', sel ? 'border-foreground bg-foreground text-background' : 'border-border text-transparent group-hover:border-foreground/40')}>
                      <Check className="size-3"/>
                    </span>
                  </button>
                  <div className={cn('flex shrink-0 items-center gap-1.5', rowPad)} style={{
                width: wOf('name', 208)
            }}>
                    <div className={cn('shrink-0 overflow-hidden rounded border border-border', thumbSize)}>
                      {a.kind === 'text' ? (<TextTile asset={a} compact/>) : (<img src={getAssetUrl(a)} alt="" loading="lazy" className="h-full w-full object-cover"/>)}
                    </div>
                    {renamingId === a.id ? (<input autoFocus data-quiet-focus defaultValue={a.name} onClick={(e)=>e.stopPropagation()} onBlur={(e)=>{
                const n = e.target.value.trim();
                if (n && n !== a.name) libraryActions.renameAsset(a.id, n);
                setRenamingId(null);
            }} onKeyDown={(e)=>{
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setRenamingId(null);
            }} className="w-full rounded bg-secondary px-1 py-0.5 text-xs text-foreground outline-none"/>) : (<span title={readOnly ? `${a.name} — read-only` : t("uk9jWc", {
                name: a.name
            })} onClick={(e)=>e.stopPropagation()} onDoubleClick={(e)=>{
                if (readOnly) return;
                e.stopPropagation();
                setRenamingId(a.id);
            }} className={cn('truncate text-foreground/90', !readOnly && 'cursor-text')}>
                        {a.name}
                      </span>)}
                  </div>
                  {columns.map((key)=>(<div key={key} className="shrink-0" style={{
                    width: wOf(key, 112)
                }}>
                      <TableCell prop={propByKey(key)} value={a.attrs[key]} rowPad={rowPad} readOnly={readOnly} onToggleCheckbox={()=>onSetValueRaw(a.id, key, a.attrs[key] === true ? false : true)} onCommitLiteral={(raw)=>{
                    const prop = propByKey(key);
                    if (prop) onSetValueRaw(a.id, key, coerceInput(prop.type, raw));
                }} onOpenPicker={(r)=>setEdit({
                        assetId: a.id,
                        key,
                        x: r.left,
                        y: r.bottom
                    })}/>
                    </div>))}
                  {mixedKinds && (<div className="w-16 px-2 text-muted-foreground">
                      {a.kind === 'image' ? t("-0zv6g") : a.kind === 'video' ? t("kBJUtE") : a.kind === 'text' ? t("aA8bDw") : t("s9xNrU")}
                    </div>)}
                </div>);
        })}
          </Fragment>))}
      </div>
      {edit && (<CellEditor label={propLabel(edit.key)} options={columnOptions(edit.key)} currentId={assets.find((x)=>x.id === edit.assetId)?.attrs[edit.key] as string | undefined} x={edit.x} y={edit.y} onPick={(value)=>{
        onSetAttr(edit.assetId, edit.key, value);
        setEdit(null);
    }} onRename={(optionId, value)=>onRenameOption(edit.key, optionId, value)} onDone={()=>setEdit(null)}/>)}
    </div>);
}
// --- Typed table cell --------------------------------------------------------
// Per-type display + inline edit for a table cell. checkbox toggles in place;
// number/text edit inline; select defers to the anchored popover.
function TableCell({ prop, value, rowPad, onToggleCheckbox, onCommitLiteral, onOpenPicker, readOnly }: {
    prop?: Property;
    value: AttrValue | undefined;
    rowPad: string;
    onToggleCheckbox: () => void;
    onCommitLiteral: (raw: string) => void;
    onOpenPicker: (rect: DOMRect) => void;
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    const [editing, setEditing] = useState(false);
    if (!prop) return <div className={cn('w-full', rowPad)}/>;
    if (readOnly) {
        return (<div className={cn('w-full truncate px-2 text-left', rowPad, hasAttrValue(value) ? 'text-muted-foreground' : 'text-muted-foreground/30')}>
        {hasAttrValue(value) ? formatAttrValue(prop, value) : t("SL-c5a")}
      </div>);
    }
    if (prop.type === 'checkbox') {
        // A non-boolean leftover (kept across a type switch) shows as its literal,
        // not a misleading unchecked box; clicking it is an informed replace.
        if (isCheckboxLeftover(value)) {
            return (<button onClick={(e)=>{
                e.stopPropagation();
                onToggleCheckbox();
            }} title={t("s4lHzQ")} className={cn('w-full truncate rounded px-2 text-left text-muted-foreground hover:bg-secondary', rowPad)}>
          {formatAttrValue(prop, value)}
        </button>);
        }
        return (<div className={cn('flex w-full items-center px-2', rowPad)}>
        <button onClick={(e)=>{
            e.stopPropagation();
            onToggleCheckbox();
        }} title={t("LaoRGw")} className={cn('flex size-3.5 items-center justify-center rounded border', value === true ? 'border-foreground bg-foreground text-background' : 'border-border text-transparent')}>
          <Check className="size-3"/>
        </button>
      </div>);
    }
    if (prop.type === 'number' || prop.type === 'text') {
        if (editing) {
            const initial = hasAttrValue(value) ? String(value) : '';
            return (<input autoFocus // A leftover under a number property rides a text input — a number
            // input would blank the invalid value, and the untouched-editor
            // guard below needs the literal to survive into e.target.value.
            type={prop.type === 'number' && (initial === '' || typeof value === 'number') ? 'number' : 'text'} defaultValue={initial} onClick={(e)=>e.stopPropagation()} onKeyDown={(e)=>{
                e.stopPropagation();
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setEditing(false);
            }} onBlur={(e)=>{
                // Untouched editor commits nothing — coercing an unedited
                // leftover would silently delete it.
                if (e.target.value !== initial) onCommitLiteral(e.target.value);
                setEditing(false);
            }} className={cn('w-full rounded bg-secondary px-2 text-xs text-foreground outline-none', rowPad)}/>);
        }
        return (<button onClick={(e)=>{
            e.stopPropagation();
            setEditing(true);
        }} title={t("wEQDC6")} className={cn('w-full truncate rounded px-2 text-left hover:bg-secondary', rowPad, hasAttrValue(value) ? 'text-muted-foreground' : 'text-muted-foreground/30')}>
        {hasAttrValue(value) ? formatAttrValue(prop, value) : t("SL-c5a")}
      </button>);
    }
    // select — anchored popover
    return (<button onClick={(e)=>{
        e.stopPropagation();
        onOpenPicker(e.currentTarget.getBoundingClientRect());
    }} title={t("wEQDC6")} className={cn('w-full truncate rounded px-2 text-left hover:bg-secondary', rowPad, hasAttrValue(value) ? 'text-muted-foreground' : 'text-muted-foreground/30')}>
      {hasAttrValue(value) ? formatAttrValue(prop, value) : t("SL-c5a")}
    </button>);
}
// --- Lightbox ----------------------------------------------------------------
/**
 * Single-asset takeover: click = look at it. Arrow keys step through the
 * caller's (filtered) order, so it doubles as a fluid browse gesture.
 */ export function AssetLightbox({ valueLabel, assets, index, heroAssetId, onStep, onClose, onSetHero }: {
    valueLabel: (key: string, value: AttrValue | undefined) => string;
    assets: LibAsset[];
    index: number;
    /** The collection's hero asset id — the current asset shows as the reference when it matches. */ heroAssetId?: string | null;
    onStep: (next: number) => void;
    onClose: () => void;
    onSetHero?: (assetId: string) => void;
}) {
    const t = useTranslations$1();
    const a = assets[index];
    useEffect(()=>{
        const onKey = (e: KeyboardEvent)=>{
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
            if (e.key === 'ArrowRight') {
                e.stopPropagation();
                onStep(Math.min(assets.length - 1, index + 1));
            }
            if (e.key === 'ArrowLeft') {
                e.stopPropagation();
                onStep(Math.max(0, index - 1));
            }
        };
        window.addEventListener('keydown', onKey, true);
        return ()=>window.removeEventListener('keydown', onKey, true);
    }, [
        onClose,
        onStep,
        index,
        assets.length
    ]);
    // --- Zoom / pan (image assets) — same interaction as the edit lightbox -----
    const boxRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{
        x: number;
        y: number;
        pan: {
            x: number;
            y: number;
        };
        moved: boolean;
    } | null>(null);
    const [view, setView] = useState<{
        zoom: number;
        pan: {
            x: number;
            y: number;
        };
    }>({
        zoom: 1,
        pan: {
            x: 0,
            y: 0
        }
    });
    const [isPanning, setIsPanning] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const isZoomed = view.zoom > 1.001;
    // Reset to fit whenever the shown asset changes (arrow-stepping, etc.).
    // Render-time adjustment (not an effect) — the repo bans setState-in-effect.
    const [prevIndex, setPrevIndex] = useState(index);
    if (prevIndex !== index) {
        setPrevIndex(index);
        setView({
            zoom: 1,
            pan: {
                x: 0,
                y: 0
            }
        });
    }
    // Zoom toward a screen point (cursor / button), keeping that point fixed.
    const zoomAt = useCallback((compute: (z: number) => number, clientX: number, clientY: number)=>{
        const box = boxRef.current;
        if (!box) return;
        setView((prev)=>{
            const z = Math.max(1, Math.min(MAX_ZOOM, compute(prev.zoom)));
            if (z <= 1) return {
                zoom: 1,
                pan: {
                    x: 0,
                    y: 0
                }
            };
            const rect = box.getBoundingClientRect();
            const mx = clientX - rect.left - rect.width / 2;
            const my = clientY - rect.top - rect.height / 2;
            const ratio = z / prev.zoom;
            const pan = clampPan({
                x: mx - ratio * (mx - prev.pan.x),
                y: my - ratio * (my - prev.pan.y)
            }, z, box);
            return {
                zoom: z,
                pan
            };
        });
    }, []);
    const zoomByStep = useCallback((factor: number)=>{
        const box = boxRef.current;
        if (!box) return;
        const rect = box.getBoundingClientRect();
        zoomAt((z)=>z * factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }, [
        zoomAt
    ]);
    const resetZoom = useCallback(()=>setView({
            zoom: 1,
            pan: {
                x: 0,
                y: 0
            }
        }), []);
    // Wheel / trackpad pinch — native, non-passive so we can preventDefault scroll.
    useEffect(()=>{
        const box = boxRef.current;
        if (!box) return;
        const onWheel = (e: WheelEvent)=>{
            e.preventDefault();
            zoomAt((z)=>z * Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
        };
        box.addEventListener('wheel', onWheel, {
            passive: false
        });
        return ()=>box.removeEventListener('wheel', onWheel);
    }, [
        zoomAt
    ]);
    const onPointerDown = useCallback((e: React.PointerEvent)=>{
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragRef.current = {
            x: e.clientX,
            y: e.clientY,
            pan: view.pan,
            moved: false
        };
        if (isZoomed) setIsPanning(true);
    }, [
        isZoomed,
        view.pan
    ]);
    const onPointerMove = useCallback((e: React.PointerEvent)=>{
        const d = dragRef.current;
        const box = boxRef.current;
        if (!d || !box) return;
        if (Math.abs(e.clientX - d.x) > 3 || Math.abs(e.clientY - d.y) > 3) d.moved = true;
        if (!isZoomed) return;
        setView((prev)=>({
                zoom: prev.zoom,
                pan: clampPan({
                    x: d.pan.x + (e.clientX - d.x),
                    y: d.pan.y + (e.clientY - d.y)
                }, prev.zoom, box)
            }));
    }, [
        isZoomed
    ]);
    const onPointerUp = useCallback((e: React.PointerEvent)=>{
        const d = dragRef.current;
        dragRef.current = null;
        setIsPanning(false);
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        // A click (no drag): zoom in to 2× at the cursor, or back to fit if zoomed.
        if (d && !d.moved) {
            if (isZoomed) resetZoom();
            else zoomAt(()=>2, e.clientX, e.clientY);
        }
    }, [
        isZoomed,
        resetZoom,
        zoomAt
    ]);
    // Download: the CDN blocks cross-origin fetch and the <a download> attribute
    // is ignored cross-origin, so route http(s) URLs through the same-origin
    // proxy (server-side fetch, CORS-free) and blob: URLs directly, then save the
    // blob with our filename. Falls back to opening the URL if the proxy denies it.
    // ponytail: reuses the existing allowlisted asset proxy; a dedicated
    // download route can replace it if the library ships.
    const displayUrl = a ? getAssetUrl(a, 'large') || getAssetUrl(a) : '';
    const currentUrl = a ? getAssetMediaUrl(a, 'large') : '';
    const onDownload = useCallback(async ()=>{
        if (!a || !currentUrl) return;
        trackLibraryAssetDownloaded({
            workspaceId: useLibraryStore.getState().workspaceId,
            assetKind: a.kind
        });
        setDownloading(true);
        try {
            const fetchUrl = currentUrl.startsWith('blob:') ? currentUrl : `/api/proxy-splat?url=${encodeURIComponent(currentUrl)}`;
            const res = await fetch(fetchUrl);
            if (!res.ok) throw new Error(String(res.status));
            const blob = await res.blob();
            const obj = URL.createObjectURL(blob);
            const ext = ((blob.type.split('/')[1] || currentUrl.split('?')[0].split('.').pop() || 'png') as string).slice(0, 5);
            const link = document.createElement('a');
            link.href = obj;
            link.download = `${(a.name || 'asset').replace(/[^\w.-]+/g, '_')}.${ext}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(()=>URL.revokeObjectURL(obj), 1000);
        } catch  {
            // Fallback only for real web URLs — an asset src is untrusted store
            // data, and window.open on an arbitrary scheme (javascript:, data:)
            // is an XSS sink.
            try {
                const parsed = new URL(currentUrl, window.location.href);
                if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
                    window.open(parsed.href, '_blank', 'noopener,noreferrer');
                }
            } catch  {
            // Unparseable URL — nothing safe to open.
            }
        } finally{
            setDownloading(false);
        }
    }, [
        a,
        currentUrl
    ]);
    if (!a) return null;
    const props = Object.entries(a.attrs).map(([key, id])=>valueLabel(key, id)).filter(Boolean).join(' · ');
    return createPortal(<div className="fixed inset-0 z-[60] flex flex-col bg-background/95 animate-in fade-in" onClick={onClose}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="min-w-0 truncate text-sm text-foreground">{a.name}</span>
        {props && props !== a.name && <span className="truncate text-xs text-muted-foreground">{props}</span>}
        <span className="ml-auto shrink-0 tabular-nums text-xs text-muted-foreground/60">
          {index + 1} / {assets.length}
        </span>
        {a.kind !== 'text' && currentUrl && (<button onClick={(e)=>{
        e.stopPropagation();
        void onDownload();
    }} disabled={downloading} aria-label={t("5q3qC0")} title={t("5q3qC0")} className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50">
            {downloading ? <Loader2 className="size-4 animate-spin"/> : <Download className="size-4"/>}
          </button>)}
        <button onClick={(e)=>{
        e.stopPropagation();
        onClose();
    }} aria-label={t("rbrahO")} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
          <X className="size-4"/>
        </button>
      </div>
      <div ref={boxRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-14 pb-4">
        <button onClick={(e)=>{
        e.stopPropagation();
        onStep(Math.max(0, index - 1));
    }} disabled={index === 0} aria-label={t("JJNc3c")} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30">
          <ChevronLeft className="size-4"/>
        </button>
        { /* ponytail: opening a text asset is guarded at the call sites; this
            renders the body so arrow-stepping onto one never shows a broken img. */ }
        {a.kind === 'text' ? (<div onClick={(e)=>e.stopPropagation()} className="max-h-full max-w-lg overflow-auto rounded border border-border bg-card p-6">
            <div className="mb-2 text-sm font-medium text-foreground">{a.name}</div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{a.textValue}</div>
          </div>) : a.kind === 'video' ? (<video src={currentUrl} poster={getAssetUrl(a) || undefined} controls playsInline preload="metadata" onClick={(e)=>e.stopPropagation()} className="max-h-full max-w-full rounded border border-border bg-card object-contain"/>) : a.kind === 'audio' ? (<div onClick={(e)=>e.stopPropagation()} className="flex w-full max-w-lg items-center rounded border border-border bg-card p-6">
            <audio src={currentUrl} controls preload="metadata" className="w-full"/>
          </div>) : (<img src={displayUrl} alt={a.name} draggable={false} onClick={(e)=>e.stopPropagation()} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} style={{
        transform: `translate(${view.pan.x}px, ${view.pan.y}px) scale(${view.zoom})`
    }} className={cn('max-h-full max-w-full touch-none select-none rounded border border-border object-contain', isPanning ? 'cursor-grabbing' : isZoomed ? 'cursor-grab' : 'cursor-zoom-in')}/>)}
        <button onClick={(e)=>{
        e.stopPropagation();
        onStep(Math.min(assets.length - 1, index + 1));
    }} disabled={index === assets.length - 1} aria-label={t("9-Ddtu")} className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-card p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30">
          <ChevronRight className="size-4"/>
        </button>
        {a.kind === 'image' && (<div onClick={(e)=>e.stopPropagation()}>
            <ImageZoomControls zoom={view.zoom} canZoomIn={view.zoom < MAX_ZOOM - 0.001} canZoomOut={isZoomed} zoomByStep={zoomByStep} reset={resetZoom}/>
          </div>)}
      </div>
      {a.kind !== 'text' && onSetHero && (<div className="flex items-center justify-center gap-2 pb-4" onClick={(e)=>e.stopPropagation()}>
          <button onClick={()=>{
        onSetHero(a.id);
    }} className={cn('rounded border border-border bg-card px-2.5 py-1 text-xs transition-colors hover:bg-secondary', a.id === heroAssetId ? 'text-muted-foreground/60' : 'text-foreground')}>
            {a.id === heroAssetId ? t("TilRBW") : t("3KTdmb")}
          </button>
        </div>)}
    </div>, document.body);
} // v1 trim: CompareOverlay and PublishOverlay removed — compare-to-pick-hero
 // and public sheets return post-v1; the lightbox covers look-at-it for now.
